// sw-register.js — service worker registration / dev-unregistration.
//
// Extracted from app.js's boot IIFE (Brief 111) so the registration logic is
// unit-testable in isolation (app.js auto-runs a huge import graph on load and
// can't be imported piecemeal). Pure: every browser global is injectable.

/**
 * Register the service worker in production, or proactively unregister any
 * leftover prod SW in dev (so a cache-first SW doesn't mask local edits).
 *
 * Always logs both outcomes — a silent registration is impossible to debug
 * from a smoke walk, and an install-time failure (e.g. a 404 shell asset) can
 * otherwise leave the page uncontrolled with nothing in the page console.
 *
 * @param {object} [deps]
 * @param {ServiceWorkerContainer} [deps.serviceWorker] - defaults to navigator.serviceWorker
 * @param {{hostname: string}} [deps.location] - defaults to window.location
 * @param {Pick<Console, "info" | "warn">} [deps.console] - defaults to console
 * @returns {Promise<ServiceWorkerRegistration | null>}
 */
export async function registerServiceWorker({
  serviceWorker = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined,
  location: loc = typeof location !== "undefined" ? location : { hostname: "" },
  console: log = console,
} = {}) {
  // No SW support (or non-secure context where the API is absent) → no-op.
  if (!serviceWorker) return null;

  // Any non-localhost host is treated as production.
  const isLocalhost = loc.hostname === "localhost"
                   || loc.hostname === "127.0.0.1"
                   || loc.hostname === "::1";

  if (isLocalhost) {
    // Drop any SW a previous prod-style load left behind, so dev edits aren't
    // masked by the cached app shell.
    try {
      const rs = await serviceWorker.getRegistrations();
      for (const r of rs) r.unregister();
    } catch { /* ignore */ }
    return null;
  }

  // Absolute "/sw.js" — a relative "./sw.js" resolves against the current
  // deep-link route (e.g. /exercise/106551 → /exercise/sw.js → 404). The SW
  // filename is stable across deploys (build.mjs emits it un-hashed) so the
  // browser's same-URL update check keeps working (Brief 103).
  try {
    const reg = await serviceWorker.register("/sw.js");
    log.info("[app] sw registered, scope:", reg.scope);
    return reg;
  } catch (err) {
    log.warn("[app] sw registration failed", err);
    return null;
  }
}
